import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { tasksAPI, projectsAPI, usersAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { 
  Plus, CheckSquare, Clock, Users, FolderOpen, Loader2, Trash2, Pencil
} from 'lucide-react';
import { TaskFilters } from '../components/tasks/TaskFilters';
import { TaskFormDialog } from '../components/tasks/TaskFormDialog';
import { DeleteTaskDialog } from '../components/tasks/DeleteTaskDialog';

export default function Tasks() {
  const { user, isAdmin, isStaff, canDelete } = useAuth();
  const navigate = useNavigate();
  
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [projectFilter, setProjectFilter] = useState('all');
  
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '', description: '', project_id: '', due_date: '',
    priority: 'medium', status: 'todo', assignees: [],
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [tasksRes, projectsRes] = await Promise.all([
        tasksAPI.list(), projectsAPI.list(),
      ]);
      setTasks(tasksRes.data);
      setProjects(projectsRes.data);
      if (isAdmin || isStaff) {
        try {
          const usersRes = isAdmin ? await usersAPI.list() : await usersAPI.teamMembers();
          setUsers(usersRes.data);
        } catch (err) {
          console.error('Failed to fetch users:', err.message);
        }
      }
    } catch (err) {
      console.error('Failed to fetch tasks:', err);
    } finally {
      setLoading(false);
    }
  }, [isAdmin, isStaff]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const resetForm = () => {
    setFormData({ title: '', description: '', project_id: '', due_date: '', priority: 'medium', status: 'todo', assignees: [] });
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      const res = await tasksAPI.create({
        ...formData,
        due_date: formData.due_date ? new Date(formData.due_date).toISOString() : null,
      });
      setTasks([res.data, ...tasks]);
      setCreateDialogOpen(false);
      resetForm();
    } catch (err) {
      console.error('Failed to create task:', err);
    } finally {
      setFormLoading(false);
    }
  };

  const handleEditTask = async (e) => {
    e.preventDefault();
    if (!selectedTask) return;
    setFormLoading(true);
    try {
      const res = await tasksAPI.update(selectedTask.task_id, {
        title: formData.title, description: formData.description,
        due_date: formData.due_date ? new Date(formData.due_date).toISOString() : null,
        priority: formData.priority, assignees: formData.assignees,
      });
      setTasks(tasks.map(t => t.task_id === selectedTask.task_id ? res.data : t));
      setEditDialogOpen(false);
      setSelectedTask(null);
      resetForm();
    } catch (err) {
      console.error('Failed to update task:', err);
    } finally {
      setFormLoading(false);
    }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      await tasksAPI.update(taskId, { status: newStatus });
      setTasks(tasks.map(t => t.task_id === taskId ? { ...t, status: newStatus } : t));
    } catch (err) {
      console.error('Failed to update task:', err);
    }
  };

  const handleDeleteTask = async () => {
    if (!selectedTask) return;
    setFormLoading(true);
    try {
      await tasksAPI.delete(selectedTask.task_id);
      setTasks(tasks.filter(t => t.task_id !== selectedTask.task_id));
      setDeleteDialogOpen(false);
      setSelectedTask(null);
    } catch (err) {
      console.error('Failed to delete task:', err);
    } finally {
      setFormLoading(false);
    }
  };

  const openEditDialog = (task) => {
    setSelectedTask(task);
    const assignees = task.assignees || (task.assignee_id ? [task.assignee_id] : []);
    setFormData({
      title: task.title, description: task.description || '',
      project_id: task.project_id,
      due_date: task.due_date ? new Date(task.due_date).toISOString().split('T')[0] : '',
      priority: task.priority, status: task.status, assignees,
    });
    setEditDialogOpen(true);
  };

  // Helpers
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'priority-high';
      case 'medium': return 'priority-medium';
      default: return 'priority-low';
    }
  };
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };
  const isOverdue = (dateString, status) => {
    if (!dateString || status === 'completed') return false;
    return new Date(dateString) < new Date();
  };
  const getProjectName = (projectId) => projects.find(p => p.project_id === projectId)?.name || 'Unknown Project';
  const getAssigneeNames = (task) => {
    const assignees = task.assignees || (task.assignee_id ? [task.assignee_id] : []);
    if (assignees.length === 0) return '-';
    return assignees.map(id => users.find(u => u.user_id === id)?.name || 'Unknown').join(', ');
  };

  // Filter & sort
  const filteredTasks = tasks.filter(task => {
    const matchesSearch = task.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || task.status === statusFilter;
    const matchesPriority = priorityFilter === 'all' || task.priority === priorityFilter;
    const matchesProject = projectFilter === 'all' || task.project_id === projectFilter;
    return matchesSearch && matchesStatus && matchesPriority && matchesProject;
  });
  const sortedTasks = [...filteredTasks].sort((a, b) => {
    if (a.status === 'completed' && b.status !== 'completed') return 1;
    if (b.status === 'completed' && a.status !== 'completed') return -1;
    const aOverdue = isOverdue(a.due_date, a.status);
    const bOverdue = isOverdue(b.due_date, b.status);
    if (aOverdue && !bOverdue) return -1;
    if (bOverdue && !aOverdue) return 1;
    if (a.due_date && b.due_date) return new Date(a.due_date) - new Date(b.due_date);
    return 0;
  });

  const staffMembers = users.filter(u => u.role !== 'client');

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6 animate-pulse">
          <div className="h-8 bg-muted w-1/4 rounded" />
          <div className="h-64 bg-muted rounded" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="tasks-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-light tracking-tight">Tasks</h1>
            <p className="text-muted-foreground mt-1">Manage and track all project tasks</p>
          </div>
          {(isAdmin || isStaff) && (
            <Button onClick={() => setCreateDialogOpen(true)} className="btn-primary" data-testid="create-task-btn">
              <Plus className="mr-2 h-4 w-4" />New Task
            </Button>
          )}
        </div>

        {/* Filters */}
        <TaskFilters
          searchTerm={searchTerm} setSearchTerm={setSearchTerm}
          statusFilter={statusFilter} setStatusFilter={setStatusFilter}
          priorityFilter={priorityFilter} setPriorityFilter={setPriorityFilter}
          projectFilter={projectFilter} setProjectFilter={setProjectFilter}
          projects={projects}
        />

        {/* Tasks Table */}
        {sortedTasks.length === 0 ? (
          <Card>
            <CardContent className="empty-state py-12">
              <CheckSquare className="h-12 w-12 text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-medium mb-2">No tasks found</h3>
              <p className="text-sm text-muted-foreground mb-4">
                {searchTerm || statusFilter !== 'all' || priorityFilter !== 'all' || projectFilter !== 'all'
                  ? 'Try adjusting your filters' : 'Create your first task to get started'}
              </p>
              {(isAdmin || isStaff) && !searchTerm && (
                <Button onClick={() => setCreateDialogOpen(true)} className="btn-primary">
                  <Plus className="mr-2 h-4 w-4" />Create Task
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {sortedTasks.map((task) => {
              const overdueTask = isOverdue(task.due_date, task.status);
              const assigneeText = getAssigneeNames(task);
              return (
                <Card
                  key={task.task_id}
                  className={`overflow-hidden ${overdueTask ? 'border-destructive/30 bg-destructive/5' : ''}`}
                  data-testid={`task-row-${task.task_id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        {/* Title + Priority */}
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <p className="font-medium text-sm leading-snug">{task.title}</p>
                          <span className={`status-badge shrink-0 ${getPriorityColor(task.priority)}`}>{task.priority}</span>
                        </div>

                        {/* Description */}
                        {task.description && (
                          <p className="text-sm text-muted-foreground mb-2 leading-relaxed">{task.description}</p>
                        )}

                        {/* Meta: project, date, assignees */}
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                          <button
                            className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors"
                            onClick={() => navigate(`/projects/${task.project_id}`)}
                          >
                            <FolderOpen className="h-3 w-3" />{getProjectName(task.project_id)}
                          </button>
                          <span className={`flex items-center gap-1 ${overdueTask ? 'text-destructive font-medium' : 'text-muted-foreground'}`}>
                            <Clock className="h-3 w-3" />{formatDate(task.due_date)}{overdueTask && ' (Overdue)'}
                          </span>
                          {assigneeText && assigneeText !== '-' && (
                            <span className="flex items-center gap-1 text-muted-foreground">
                              <Users className="h-3 w-3" />{assigneeText}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    {(isAdmin || isStaff) && (
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-border/50">
                        <Select value={task.status} onValueChange={(value) => handleStatusChange(task.task_id, value)}>
                          <SelectTrigger className="w-32 h-8 text-xs" data-testid={`task-status-select-${task.task_id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="todo">To Do</SelectItem>
                            <SelectItem value="in_progress">In Progress</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                          </SelectContent>
                        </Select>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditDialog(task)} data-testid={`edit-task-${task.task_id}`}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive"
                            onClick={() => { setSelectedTask(task); setDeleteDialogOpen(true); }}
                            data-testid={`delete-task-${task.task_id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Summary Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card><CardContent className="p-4 text-center"><p className="text-2xl font-light">{tasks.length}</p><p className="text-xs uppercase tracking-wider text-muted-foreground">Total</p></CardContent></Card>
          <Card><CardContent className="p-4 text-center"><p className="text-2xl font-light">{tasks.filter(t => t.status === 'todo').length}</p><p className="text-xs uppercase tracking-wider text-muted-foreground">To Do</p></CardContent></Card>
          <Card><CardContent className="p-4 text-center"><p className="text-2xl font-light">{tasks.filter(t => t.status === 'in_progress').length}</p><p className="text-xs uppercase tracking-wider text-muted-foreground">In Progress</p></CardContent></Card>
          <Card><CardContent className="p-4 text-center"><p className="text-2xl font-light">{tasks.filter(t => t.status === 'completed').length}</p><p className="text-xs uppercase tracking-wider text-muted-foreground">Completed</p></CardContent></Card>
        </div>

        {/* Dialogs */}
        <TaskFormDialog
          open={createDialogOpen} onOpenChange={setCreateDialogOpen}
          formData={formData} setFormData={setFormData}
          onSubmit={handleCreateTask} formLoading={formLoading}
          projects={projects} staffMembers={staffMembers} isEdit={false}
        />
        <TaskFormDialog
          open={editDialogOpen} onOpenChange={setEditDialogOpen}
          formData={formData} setFormData={setFormData}
          onSubmit={handleEditTask} formLoading={formLoading}
          projects={projects} staffMembers={staffMembers} isEdit={true}
        />
        <DeleteTaskDialog
          open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}
          taskTitle={selectedTask?.title}
          onConfirm={handleDeleteTask} loading={formLoading}
        />
      </div>
    </Layout>
  );
}
